// The workflow run pages' small pure helpers (agent workflows W1).
import type { BadgeTone } from '../components/badge-tokens.js';
import type { FlowNodeState } from '../components/cos-flow-canvas.js';
import type { DecisionAnswer, DecisionQuestion, WorkflowDecision, WorkflowHiddenValue, WorkflowTimelineStep } from '../types/workflows.js';

export const WORKFLOW_RUN_TONE: Record<string, BadgeTone> = {
	active: 'blue',
	paused: 'amber',
	succeeded: 'green',
	failed: 'red',
	cancelled: 'grey',
};

export const WORKFLOW_STEP_TONE: Record<string, BadgeTone> = {
	ready: 'grey',
	running: 'blue',
	waiting: 'amber',
	succeeded: 'green',
	failed: 'red',
	timed_out: 'red',
	cancelled: 'grey',
	skipped: 'grey',
};

const STEP_TO_NODE_STATE: Record<string, FlowNodeState> = {
	ready: 'queued',
	running: 'running',
	waiting: 'waiting',
	succeeded: 'done',
	failed: 'failed',
	timed_out: 'failed',
	cancelled: 'cancelled',
	skipped: 'skipped',
};

/** Each node's state on the run graph: its latest visit's step, by node key. A node with no step has no state. */
export function nodeStatesFromSteps(steps: WorkflowTimelineStep[]): Record<string, FlowNodeState> {
	const latest = new Map<string, WorkflowTimelineStep>();
	for (const st of steps) {
		const cur = latest.get(st.node_key);
		if (!cur || st.visit > cur.visit || (st.visit === cur.visit && st.id > cur.id)) latest.set(st.node_key, st);
	}
	const out: Record<string, FlowNodeState> = {};
	for (const [key, st] of latest) {
		const s = STEP_TO_NODE_STATE[st.status];
		if (s) out[key] = s;
	}
	return out;
}

/** A duration in words: "under a second", "12 seconds", "3 minutes", "2 hours". */
export function durationText(ms: number | null | undefined): string {
	if (ms === null || ms === undefined || ms < 0) return '';
	if (ms < 1000) return 'under a second';
	const s = Math.round(ms / 1000);
	if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
	const m = Math.round(s / 60);
	if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
	const h = Math.round(m / 60);
	if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`;
	const d = Math.round(h / 24);
	return `${d} days`;
}

/** A step's JSON for the collapsible view; empty when there is none. */
export function prettyJSON(v: unknown): string {
	if (v === null || v === undefined) return '';
	if (typeof v === 'object' && Object.keys(v as object).length === 0) return '';
	return JSON.stringify(v, null, 2);
}

/** A value the server hid from this viewer (PRD 17.5). */
export function isHiddenValue(v: unknown): v is WorkflowHiddenValue {
	if (typeof v !== 'object' || v === null) return false;
	const h = v as { hidden?: unknown; reason?: unknown };
	return h.hidden === true && typeof h.reason === 'string';
}

/** "Hidden: needs access to client Northfield Library". */
export function hiddenText(v: WorkflowHiddenValue): string {
	return `Hidden: ${v.reason}`;
}

/** "Run 12 of Code review". */
export function runTitle(number: number, workflow: string): string {
	return `Run ${number} of ${workflow}`;
}

// ── Decisions (agent workflows W2) ───────────────────────────────────


/** A probability as a whole percentage: 0.834 is "83%". */
export function pct(v: number | null | undefined): string {
	if (v === null || v === undefined || Number.isNaN(v)) return 'none';
	return `${Math.round(v * 100)}%`;
}

/** One answer in plain words, against the question's bar. */
export function answerText(q: DecisionQuestion | undefined, a: DecisionAnswer | undefined): string {
	if (!a) return 'no answer';
	const th = q?.threshold ?? {};
	switch (a.kind) {
		case 'yes_no': {
			const p = a.p_yes ?? 0;
			const yesAt = th.yes_at ?? 0.5;
			const noAt = th.no_at ?? yesAt;
			const bar = noAt < yesAt ? `yes at ${pct(yesAt)} or more, no at ${pct(noAt)} or less` : `yes at ${pct(yesAt)} or more`;
			return `${pct(p)} likely yes (${bar})`;
		}
		case 'pick_one': {
			const min = th.min ?? 0;
			return `${a.choice ?? 'nothing'}, ${pct(a.confidence)} sure${min > 0 ? ` (needs ${pct(min)})` : ''}`;
		}
		default: {
			const min = th.min ?? 0;
			const score = a.score === undefined ? 'no score' : `score ${a.score.toFixed(2)}`;
			return `${score}, ${pct(a.confidence)} sure${min > 0 ? ` (needs ${pct(min)})` : ''}`;
		}
	}
}

/** Who answered: "Live: jev (jev-2026-10)", "Shadow: openai-trial", "Fallback: agent decider-one". */
export function decisionWho(d: WorkflowDecision): string {
	const mode = d.mode === 'live' ? 'Live' : d.mode === 'shadow' ? 'Shadow' : 'Fallback';
	const engine = d.engine.startsWith('agent:') ? `agent ${d.engine.slice('agent:'.length)}` : d.engine;
	const model = d.model && d.model !== 'agent' ? ` (${d.model})` : '';
	return `${mode}: ${engine}${model}`;
}

/** What became of a decision, in a word or two. */
export function decisionOutcome(d: WorkflowDecision): string {
	if (d.error) return 'Failed';
	if (d.used) return 'Chose the route';
	if (d.mode === 'shadow') return d.confidence_ok ? 'Compared only' : 'Compared only, unsure';
	return d.confidence_ok ? 'Not used' : 'Unsure';
}

export const WORKFLOW_DEFINITION_TONE: Record<string, BadgeTone> = {
	draft: 'amber',
	active: 'green',
	retired: 'grey',
};

/** Hands the viewer a workflow document as a .json file (the definitions list's Export, the editor's copy of unsaved work). */
export function downloadWorkflowJSON(filename: string, doc: unknown): void {
	const blob = new Blob([`${JSON.stringify(doc, null, 2)}\n`], { type: 'application/json' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** The server's slug rule (service.workflowSlugRe): 2 to 64 lowercase letters, digits and hyphens, starting with a letter. */
export const WORKFLOW_SLUG_RE = /^[a-z][a-z0-9-]{1,63}$/;

/** "code-review" from "Code review", or '' until the title makes a slug the server would accept (a one-letter title does not). */
export function slugFromTitle(title: string): string {
	const slug = title
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^[^a-z]+/, '')
		.replace(/-+$/, '')
		.slice(0, 64)
		.replace(/-+$/, '');
	return WORKFLOW_SLUG_RE.test(slug) ? slug : '';
}

// One publish "widening" label (service.WorkflowService.widening) as a plain
// sentence. `titles` maps a step key to its title; `words` gives the served
// vocabulary's labels (GET /workflow-vocabulary) for trigger kinds and who
// may start, so the sentence uses the words the Triggers tab shows. The
// labels: `run_as_changed: A to B`, `tool_action_added: tool (step_key)`,
// `trigger_added: kind (roles)` and `trigger_resumed: kind (roles)` (the
// roles only for a kind people start), `start_roles_widened: A to B`,
// `start_users_added: 12, 34`, `human_removed: step_key (title)` and
// `required_lowered: step_key (A to B)` (B may be `path`: read from the run).
export interface WideningWords {
	triggerKinds?: Record<string, string>;
	startRoles?: Record<string, string>;
}

export function wideningSentence(label: string, titles: Record<string, string> = {}, words: WideningWords = {}): string {
	const agent = (a: string) => (a === 'none' ? 'no agent' : a);
	const lower = (t: string) => (t ? t[0].toLowerCase() + t.slice(1) : t);
	const kindWords = (k: string) => lower(words.triggerKinds?.[k] ?? k.replace(/_/g, ' '));
	const roleWords = (r: string) => lower(words.startRoles?.[r] ?? r.replace(/_/g, ' '));
	const runAs = /^run_as_changed: (.+?) to (.+)$/.exec(label);
	if (runAs) {
		const [, was, now] = runAs;
		return now === 'none' ? `No longer runs as an agent (was: ${agent(was)})` : `Runs as ${now} (was: ${agent(was)})`;
	}
	const tool = /^tool_action_added: (.+?) \((.+)\)$/.exec(label);
	if (tool) {
		const [, name, key] = tool;
		return `Now acts through the tool ${name} (step '${titles[key] || key}')`;
	}
	const trigger = /^trigger_(added|resumed): ([a-z_]+)(?: \(([a-z_]+)\))?$/.exec(label);
	if (trigger) {
		const [, how, kind, roles] = trigger;
		const who = roles ? ` (who may start it: ${roleWords(roles)})` : '';
		return `${how === 'added' ? 'Runs can now start' : 'Runs can start again'} ${kindWords(kind)}${who}`;
	}
	const widened = /^start_roles_widened: ([a-z_]+) to ([a-z_]+)$/.exec(label);
	if (widened) {
		return `More people may start it: ${roleWords(widened[2])} (was: ${roleWords(widened[1])})`;
	}
	const users = /^start_users_added: (.+)$/.exec(label);
	if (users) {
		const n = users[1].split(',').length;
		return `More people may start it: ${n === 1 ? 'one person' : `${n} people`} added to the list`;
	}
	const removed = /^human_removed: ([a-z0-9_]+) \((.*)\)$/.exec(label);
	if (removed) {
		return `No longer asks people at step '${removed[2] || removed[1]}'`;
	}
	const fewer = /^required_lowered: ([a-z0-9_]+) \((\d+) to (\d+|path)\)$/.exec(label);
	if (fewer) {
		const [, key, was, now] = fewer;
		const step = titles[key] || key;
		return now === 'path'
			? `How many people must approve at step '${step}' now comes from the run (was: ${was})`
			: `Fewer people must approve at step '${step}': ${now} (was: ${was})`;
	}
	const rest = /^([a-z_]+): (.*)$/.exec(label);
	return rest ? `${rest[1].replace(/_/g, ' ')}: ${rest[2]}` : label;
}
